import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Flex, Text } from '@chakra-ui/react'

import Camera, { CamerRefHandle } from 'components/partials/shared/Camera/Camera'
import ImagesSlider from 'components/partials/shared/ImagesSlider/ImagesSlider'
import ImageWithPreview from 'components/partials/shared/ImageWithPreview'
import { ButtonText, LeidButton, LeidButtonWhiteOutlined } from 'components/ui/Buttons/Button'

import { TakePhotosStepData } from '../models'
import { TakePhotoStepImageData } from '../utils/models'

type Props = {
  data: TakePhotosStepData
  setCapturedImages: (images: TakePhotoStepImageData[]) => void
  onStepSuccess: () => void
}

const TakePhotosStep = (props: Props): JSX.Element => {
  const { setCapturedImages, onStepSuccess, data } = props
  const { imageSequenceSteps } = data

  const { t } = useTranslation('common')

  const cameraRef = useRef<CamerRefHandle>(null)

  const [images, setImages] = useState<Array<TakePhotoStepImageData>>([])
  const [activeSequenceStepIndex, setActiveSequenceStepIndex] = useState<number | undefined>(
    imageSequenceSteps.length !== 0 ? 0 : undefined,
  )

  const handlePhotoDelete = (index: number) => {
    const newImages = images.filter((_, imageIndex) => imageIndex !== index)
    setImages(newImages)

    chooseNextActiveStep(newImages)
  }

  const handleSendPhotos = () => {
    setCapturedImages(images)
    onStepSuccess()
  }

  const handleCameraChange = () => {
    cameraRef.current?.changeCamera()
  }

  const handleTakePicture = async () => {
    if (cameraRef.current) {
      const image = await cameraRef.current.capture()

      if (image) {
        const step = activeSequenceStepIndex !== undefined ? imageSequenceSteps[activeSequenceStepIndex] : undefined
        const fullImageData: TakePhotoStepImageData = {
          ...image,
          sequenceIndex: activeSequenceStepIndex,
          sequenceStepId: step?.id,
        }

        const newImages = [...images, fullImageData]
        setImages(newImages)

        chooseNextActiveStep(newImages)
      }
    }
  }

  const chooseNextActiveStep = (images: TakePhotoStepImageData[]) => {
    const notYetReadyStep = imageSequenceSteps.find((step) => images.every((i) => i.sequenceStepId !== step.id))
    setActiveSequenceStepIndex(notYetReadyStep ? imageSequenceSteps.indexOf(notYetReadyStep) : undefined)
  }

  const handleSkipClick = () => {
    setCapturedImages([])
    onStepSuccess()
  }

  const isConfirmDisabled = () => {
    if (images.length === 0) {
      return true
    }

    if (props.data.imageSequenceSteps.some((s) => images.find((i) => i.sequenceStepId === s.id) === undefined)) {
      return true
    }

    return false
  }

  return (
    <>
      <Flex
        position="relative"
        flexDirection="column"
        justifyContent="safe center"
        alignItems="center"
        width="100%"
        gridGap="12px"
        height="40vh"
        // overflow="auto"
      >
        <Box position="absolute" zIndex="max" style={{ top: 0, right: 0, margin: '10px' }}>
          <LeidButtonWhiteOutlined onClick={handleCameraChange}>{t('changeCamera')}</LeidButtonWhiteOutlined>
        </Box>
        {activeSequenceStepIndex !== undefined && imageSequenceSteps[activeSequenceStepIndex].imageUrl && (
          <Box position="absolute" zIndex="max" style={{ top: 50, right: 0, margin: '10px' }}>
            <ImageWithPreview imageUrl={imageSequenceSteps[activeSequenceStepIndex].imageUrl} />
          </Box>
        )}

        <Camera ref={cameraRef} />
      </Flex>

      <LeidButton onClick={handleTakePicture}>{t('takePicture')}</LeidButton>

      {activeSequenceStepIndex !== undefined && (
        <>
          <Text fontSize="md">{imageSequenceSteps[activeSequenceStepIndex].captureInstructions}</Text>

          <Flex gap={2}>
            {imageSequenceSteps.map((step, index) => (
              <Box
                key={step.id}
                textAlign="center"
                width="24px"
                height="24px"
                borderRadius="full"
                borderWidth="1px"
                borderColor="green.a200"
                backgroundColor={images.some((s) => s.sequenceStepId === step.id) ? 'green.a200' : 'white'}
                color={images.some((s) => s.sequenceStepId === step.id) ? 'white' : 'green.a200'}
              >
                {index + 1}
              </Box>
            ))}
          </Flex>
        </>
      )}

      <ImagesSlider images={images} handleImageDelete={handlePhotoDelete} />

      <LeidButton onClick={handleSendPhotos} isDisabled={isConfirmDisabled()}>
        {t('confirmImages')}
      </LeidButton>

      <Box width="100%" display="flex" justifyContent="end">
        <ButtonText onClick={handleSkipClick} isSmall isDimmed>
          {t('skip')}
        </ButtonText>
      </Box>
    </>
  )
}

export default TakePhotosStep
